import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '#client/lib/cn';

export type BannerTone = 'info' | 'success' | 'warning' | 'severe' | 'critical';

interface BannerProps {
  tone?: BannerTone | undefined;
  title?: ReactNode | undefined;
  onDismiss?: (() => void) | undefined;
  children?: ReactNode | undefined;
  className?: string | undefined;
}

/**
 * A leading icon per tone, so the meaning does not rest on colour alone.
 * `severe` and `critical` share a glyph deliberately — they differ in urgency,
 * not in kind, and inventing a third symbol would be noise.
 */
const TONE_ICON: Record<BannerTone, ReactNode> = {
  info: <Info />,
  success: <CircleCheck />,
  warning: <TriangleAlert />,
  severe: <TriangleAlert />,
  critical: <CircleAlert />,
};

export function Banner({ tone = 'info', title, onDismiss, children, className }: BannerProps) {
  return (
    <div
      className={cn('c-banner', `c-banner--${tone}`, className)}
      /*
       * A critical banner is almost always a validation failure or a request
       * that did not go through — something the user must know about before
       * continuing, so it interrupts. Everything else is announced politely
       * when the screen reader next pauses.
       */
      role={tone === 'critical' ? 'alert' : 'status'}
    >
      <span className="c-banner__icon" aria-hidden="true">
        {TONE_ICON[tone]}
      </span>

      <div className="c-banner__content">
        {title && <p className="c-banner__title">{title}</p>}
        {children && <div className="c-banner__body">{children}</div>}
      </div>

      {onDismiss && (
        <button
          type="button"
          className="c-banner__dismiss"
          onClick={onDismiss}
          aria-label="Dismiss"
        >
          <X aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
